import { CalendarDays, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { GetEventResponse } from '~entities/events/api/get-event';
import { useUpdateEventAttendanceDates } from '~entities/student/api/update-event-attendance-dates';

import {
  effectiveAttendanceSlotCount,
  getAttendanceOptions,
  scaleAttendanceTuition,
  shouldAllowAttendanceDateSelection,
  toAttendanceDateKey,
} from '~shared/helpers/attendance-dates';
import { useGetCurrency } from '~shared/api/currency-api';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';
import { Switch } from '~shared/shadcn/ui/switch';

interface EditAttendanceDatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentId: string;
  studentName: string;
  eventId: string;
  eventData: GetEventResponse;
  currentSelectedDates: string[];
  onSuccess?: () => void;
}

export const EditAttendanceDatesDialog = ({
  open,
  onOpenChange,
  studentId,
  studentName,
  eventId,
  eventData,
  currentSelectedDates,
  onSuccess,
}: EditAttendanceDatesDialogProps) => {
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const updateAttendanceDates = useUpdateEventAttendanceDates();
  const attendanceOptions = useMemo(
    () => getAttendanceOptions(eventData),
    [eventData],
  );
  const allowSelection = shouldAllowAttendanceDateSelection(eventData);

  // true = student is attending only specific dates (not all)
  const [selectSpecificDates, setSelectSpecificDates] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;

    const hasSpecificDates = currentSelectedDates.length > 0;
    setSelectSpecificDates(hasSpecificDates);

    if (hasSpecificDates) {
      // Only keep dates that are still in the future (filtered by utility)
      const availableKeys = new Set(attendanceOptions.map((o) => o.key));
      const filtered = currentSelectedDates
        .map((d) => toAttendanceDateKey(d))
        .filter((k) => availableKeys.has(k));
      setSelectedKeys(new Set(filtered));
    } else {
      setSelectedKeys(new Set(attendanceOptions.map((o) => o.key)));
    }
  }, [open, currentSelectedDates, attendanceOptions]);

  const handleToggleSpecificDates = (checked: boolean) => {
    setSelectSpecificDates(checked);
    if (checked) {
      setSelectedKeys(new Set(attendanceOptions.map((o) => o.key)));
    } else {
      setSelectedKeys(new Set());
    }
  };

  const toggleDate = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectAll = () =>
    setSelectedKeys(new Set(attendanceOptions.map((o) => o.key)));

  const clearAll = () => setSelectedKeys(new Set());

  const handleSave = async () => {
    if (selectSpecificDates && selectedKeys.size === 0) {
      toast.error('Please select at least one attendance date.');
      return;
    }

    const isFullAttendance =
      !selectSpecificDates || selectedKeys.size === attendanceOptions.length;

    const selectedDates = isFullAttendance
      ? []
      : attendanceOptions
          .filter((o) => selectedKeys.has(o.key))
          .map((o) => o.isoDate);

    try {
      await updateAttendanceDates.mutateAsync({
        studentId,
        eventId,
        selectedScheduleDates: selectedDates,
      });

      toast.success(`Attendance dates updated for ${studentName}.`);
      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? 'Failed to update attendance dates.',
      );
    }
  };

  if (!allowSelection) {
    return null;
  }

  const selectedCount = selectedKeys.size;
  const totalCount = attendanceOptions.length;

  const tuitionFee = Number(eventData.tuitionFee ?? 0);
  const baselineSlots = effectiveAttendanceSlotCount(
    currentSelectedDates,
    attendanceOptions,
  );
  const newSlotCount =
    !selectSpecificDates || selectedCount === totalCount
      ? totalCount
      : selectedCount;
  const fromTuition = scaleAttendanceTuition(
    tuitionFee,
    totalCount,
    baselineSlots,
  );
  const toTuition = scaleAttendanceTuition(tuitionFee, totalCount, newSlotCount);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <CalendarDays className='size-5' />
            Edit Attendance Dates
          </DialogTitle>
          <p className='text-sm text-muted-foreground'>
            {studentName} &mdash; {eventData.title}
          </p>
        </DialogHeader>

        <div className='space-y-4'>
          {/* Toggle */}
          <div className='flex items-center justify-between rounded-lg border p-3'>
            <div>
              <p className='text-sm font-medium'>Select specific dates</p>
              <p className='text-xs text-muted-foreground'>
                {selectSpecificDates
                  ? 'Choose which dates apply to this student.'
                  : 'Student attends all available dates.'}
              </p>
            </div>
            <Switch
              checked={selectSpecificDates}
              onCheckedChange={handleToggleSpecificDates}
            />
          </div>

          {tuitionFee > 0 && totalCount > 0 && (
            <div className='rounded-md border bg-muted/40 p-3 text-sm'>
              <p className='font-medium'>Tuition impact (estimate)</p>
              <p className='mt-1 text-xs text-muted-foreground'>
                Prorated from the event tuition and the number of future dates
                selected. Recurring event subscriptions and upcoming invoices
                update when you save, where applicable.
              </p>
              <p className='mt-2 tabular-nums'>
                {fromTuition === toTuition ? (
                  <>
                    <span className='text-muted-foreground'>No change: </span>
                    {currencySymbol}
                    {toTuition.toFixed(2)}
                  </>
                ) : (
                  <>
                    <span className='text-muted-foreground'>From </span>
                    {currencySymbol}
                    {fromTuition.toFixed(2)}
                    <span className='text-muted-foreground'> to </span>
                    {currencySymbol}
                    {toTuition.toFixed(2)}
                  </>
                )}
              </p>
            </div>
          )}

          {/* Date list — only shown when toggle is ON */}
          {selectSpecificDates && (
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <Badge variant='secondary'>
                  {selectedCount} of {totalCount} selected
                </Badge>
                <div className='flex gap-2'>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={selectAll}
                    disabled={selectedCount === totalCount}
                    className='h-7 px-2 text-xs'
                  >
                    Select all
                  </Button>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={clearAll}
                    disabled={selectedCount === 0}
                    className='h-7 px-2 text-xs'
                  >
                    Clear all
                  </Button>
                </div>
              </div>

              <div className='max-h-64 overflow-y-auto rounded-md border p-3 space-y-2'>
                {attendanceOptions.map((option) => (
                  <div key={option.key} className='flex items-center gap-3'>
                    <Checkbox
                      id={`date-${option.key}`}
                      checked={selectedKeys.has(option.key)}
                      onCheckedChange={() => toggleDate(option.key)}
                    />
                    <Label
                      htmlFor={`date-${option.key}`}
                      className='cursor-pointer font-normal'
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={updateAttendanceDates.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              updateAttendanceDates.isPending ||
              (selectSpecificDates && selectedCount === 0)
            }
          >
            {updateAttendanceDates.isPending ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Saving…
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
